import './xeroLineItemsSection.scss';

import { Identifiable, Reference } from '@approvalmax/types';
import {
    Button,
    Checkbox,
    DropdownEditor,
    FocusImplementer,
    NumberEditor,
    prioritizeAndMapDropdownItems,
    TextAreaEditor,
} from '@approvalmax/ui';
import { Tooltip } from '@approvalmax/ui/src/components';
import { intl, numberHelpers, stringHelpers } from '@approvalmax/utils';
import { useAtomValue } from 'jotai';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';
import { constants, selectors, statics } from 'modules/common';
import { domain } from 'modules/data';
import { DiscountType } from 'modules/data/backend';
import { PlainDataProvider } from 'modules/data-providers';
import { useDispatch, useSelector } from 'modules/react-redux';
import { PriceCheckIndicator, WarningLevel } from 'modules/request';
import { TwoDifferentValuesWarning } from 'modules/request';
import { WarningIndicator } from 'modules/request/components/WarningIndicator/WarningIndicator';
import { calculateXeroLineItemExpectedPrice } from 'modules/utils';
import { LineItemsGroupEdit, ReadOnlyCell } from 'pages/requestForm/components';
import { ExchangeRateWithUpdate } from 'pages/requestForm/components/ExchangeRateWithUpdate/ExchangeRateWithUpdate';
import XeroDiscountCell from 'pages/requestForm/components/lineItemsSection/XeroDiscountCell/XeroDiscountCell';
import { checkFieldValueWithRules } from 'pages/requestForm/data/Request';
import { useOcrData } from 'pages/requestForm/hooks/useOcrData';
import { FC, useCallback, useMemo, useRef } from 'react';
import bemFactory from 'react-bem-factory';
import { ApiTypes } from 'shared/data/webApp/v2';
import { getContactDisplayNameByIntegrationCode } from 'shared/helpers';
import { useUserBetaFeatures } from 'shared/hooks';
import { ocrIsLoadingState } from 'shared/states';
import styled from 'styled-components';

import {
    addXeroLineItem,
    changeCurrency,
    changeExchangeRateParams,
    changeXeroLineAmountType,
    changeXeroLineItemAllChecked,
    changeXeroLineItemChecked,
    changeXeroLineItemDescription,
    changeXeroLineItemDiscount,
    changeXeroLineItemQty,
    changeXeroLineItemsAccount,
    changeXeroLineItemsItem,
    changeXeroLineItemsTax,
    changeXeroLineItemsTrackingCategoryValue,
    changeXeroLineItemTaxAmount,
    changeXeroLineItemUnitPrice,
    cloneXeroLineItems,
    ExchangeRateParams,
    removeXeroLineItems,
    reorderXeroLineItems,
} from '../../../actions';
import { LineItemsTable } from '../../../components';
import { CurrencyDisclaimerLegacy } from '../../../components/(legacy)/CurrencyDisclaimerLegacy/CurrencyDisclaimerLegacy';
import CurrencyExchangeRateInput from '../../../components/CurrencyExchangeRateInput/CurrencyExchangeRateInput';
import { SectionContainer } from '../../../components/SectionContainer';
import { findLineAmountType, lineAmountTypeReferences } from '../../../data/xero/statics/lineAmountType';
import { XeroCurrency } from '../../../data/xero/XeroCurrency';
import { useGetRecognizedOcrAttachment } from '../../../hooks/useGetRecognizedOcrAttachment';
import { getContext, getShowValidationErrors } from '../../../selectors/pageSelectors';
import {
    getFieldsApprovalPermissionById,
    getFieldsApprovalPermissionBySystemPurpose,
    getIsExchangeRateInputAvailable,
    getReadonlyFields,
    getRequestEditMode,
    getRequiredFields,
    RequestEditMode,
} from '../../../selectors/requestSelectors';
import { ColumnDefinition } from '../../../selectors/types/ColumnDefinition';
import { ExpandedXeroLineItem } from '../../../selectors/types/ExpandedXeroLineItem';
import { getXeroContext, getXeroLineItems } from '../../../selectors/xero';
import { XeroLineItemsSectionFooterLegacy } from '../(legacy)/XeroLineItemsSectionFooterLegacy/XeroLineItemsSectionFooterLegacy';
import { XeroLineItemDropdownCell } from '../XeroLineItemDropdownCell/XeroLineItemDropdownCell';
import { XeroLineItemItemCell } from '../XeroLineItemItemCell/XeroLineItemItemCell';
import { XeroRequestItemsSummary } from '../XeroRequestItemsSummary/XeroRequestItemsSummary';
import { filterTaxCodesByAccountClass } from './XeroLineItemsSection.helpers';
import { useGroupEditItems } from './XeroLineItemsSection.hooks';
import { messages } from './XeroLineItemsSection.messages';
import { XeroLineItemsSectionProps } from './XeroLineItemsSection.types';

const { xeroConstants } = constants;

const bem = bemFactory.block('reqf-xero-line-items-section');
const qa = bemFactory.qa('line-items-section');

const StyledCurrencyDisclaimer = styled(CurrencyDisclaimerLegacy)`
    margin: 0 30px;
    text-align: right;
`;

const StyledExchangeRateWithUpdate = styled(ExchangeRateWithUpdate)`
    margin: 0 30px;
    text-align: right;
`;

const XeroLineItemsSection: FC<XeroLineItemsSectionProps> = (props) => {
    const { request, className } = props;

    const ocrIsLoading = useAtomValue(ocrIsLoadingState);

    const { details, integrationCode, currency, companyCurrency, exchangeRate } = request;
    const { contact, lineAmountType } = details;

    const { isBelgradeUILineItemsTotalSection } = useUserBetaFeatures();

    const xeroContext = useSelector(getXeroContext);
    const context = useSelector(getContext);
    const requiredFields = useSelector((state) => getRequiredFields(state, request));
    const editMode = useSelector((state) => getRequestEditMode(state, request));
    const isEditorMode = editMode === RequestEditMode.Editor;
    const isApproverMode = editMode === RequestEditMode.Approver;
    const lineItems = useSelector((state) => getXeroLineItems(state, request));
    const readonlyFields = useSelector((state) => getReadonlyFields(state, request));
    const showValidationErrors = useSelector(getShowValidationErrors);

    const dispatch = useDispatch();
    const me = useSelector(selectors.profile.getProfileUser);
    const isExchangeRateInputAvailable = getIsExchangeRateInputAvailable(request);
    const participantWithEditPermission = useMemo(() => {
        const activeStep = selectors.request.getActiveStep(request);

        return selectors.request.getParticipantWithEditPermission(me.id, activeStep?.participants);
    }, [me.id, request]);

    const ocrAttachment = useGetRecognizedOcrAttachment(request);

    const { data: ocrData } = useOcrData(request, ocrAttachment?.id);

    const sortedOcrLineItems = useMemo(
        () =>
            ocrData &&
            (sortBy(
                ocrData.document?.lineItems,
                'lineOrder'
            ) as ApiTypes['OcrContractsModelsXeroRecognizedLineItemAnswerCamelCased'][]),
        [ocrData]
    );

    const initialCurrencyValue = useRef(statics.currency.findCurrencyByCode(request.currency));
    const initialLineAmountType = useRef(request.details.lineAmountType);
    const initialLineItems = useRef(lineItems);

    const getInitialLineItem = useCallback((lineItemId: string) => {
        return initialLineItems.current.find((lineItem) => lineItem.id === lineItemId);
    }, []);

    const [
        exchangeRateFieldPermission,
        currencyFieldPermission,
        lineAmountTypeFieldPermission,
        itemFieldPermission,
        descriptionFieldPermission,
        accountFieldPermission,
        taxFieldPermission,
        taxAmountFieldPermission,
        discountFieldPermission,
        qtyFieldPermission,
        unitPriceFieldPermission,
    ] = useSelector((state) =>
        getFieldsApprovalPermissionBySystemPurpose(state, request, [
            domain.FieldSystemPurpose.XeroManualExchangeRate,
            domain.FieldSystemPurpose.XeroCurrency,
            domain.FieldSystemPurpose.XeroLineAmountType,
            domain.FieldSystemPurpose.XeroItem,
            domain.FieldSystemPurpose.XeroLineDescription,
            domain.FieldSystemPurpose.XeroAccount,
            domain.FieldSystemPurpose.XeroTax,
            domain.FieldSystemPurpose.XeroLineTaxAmount,
            domain.FieldSystemPurpose.XeroLineDiscount,
            domain.FieldSystemPurpose.XeroLineQty,
            domain.FieldSystemPurpose.XeroLineUnitPrice,
        ])
    );
    const fieldsId = useMemo(
        () => xeroContext.trackingCategories.map((tc) => tc.category.id),
        [xeroContext.trackingCategories]
    );
    const trackingCategoriesPermissions = useSelector((state) =>
        getFieldsApprovalPermissionById(state, request, fieldsId)
    );

    const xeroItemReadonly = useMemo(() => {
        const xeroItemField = context.fields.find((f) => f.systemPurpose === domain.FieldSystemPurpose.XeroItem);

        return Boolean(xeroItemField && context.readonlyFieldIds.includes(xeroItemField.id));
    }, [context.fields, context.readonlyFieldIds]);

    const getItemReadonly = useCallback<() => boolean>(
        () => xeroItemReadonly || ocrIsLoading || itemFieldPermission.disabled,
        [itemFieldPermission.disabled, ocrIsLoading, xeroItemReadonly]
    );

    const getAccountReadonly = useCallback(
        (li: (typeof lineItems)[number]) => li.accountIsReadonly || ocrIsLoading || accountFieldPermission.disabled,
        [accountFieldPermission.disabled, ocrIsLoading]
    );

    const getTaxRateReadonly = useCallback<() => boolean>(
        () =>
            request.details.lineAmountType === domain.LineAmountType.NoTax ||
            ocrIsLoading ||
            taxFieldPermission.disabled,
        [ocrIsLoading, request.details.lineAmountType, taxFieldPermission.disabled]
    );

    const getTrackingCategoryReadonly = useCallback(
        (categoryId: string) =>
            readonlyFields.fieldIds.includes(categoryId) ||
            ocrIsLoading ||
            !!trackingCategoriesPermissions[categoryId]?.disabled,
        [ocrIsLoading, readonlyFields.fieldIds, trackingCategoriesPermissions]
    );

    const {
        groupItems,
        isShowGroupButtons,
        handleGroupRemove,
        handleGroupClone,
        openGroupEdit,
        closeGroupEdit,
        isGroupEditVisible,
        isGroupCloneVisible,
        isGroupRemoveVisible,
        isGroupEditPopupVisible,
    } = useGroupEditItems({
        contact,
        xeroContext,
        itemVisible: !getItemReadonly(),
        taxRateVisible: !getTaxRateReadonly(),
        isEditorMode,
        lineItems,
        getAccountReadonly,
        getTrackingCategoryReadonly,
    });

    const templateName = selectors.template.getTemplateDisplayNameByCode(request.integrationCode);

    const tableDef = useMemo(() => {
        const itemField = context.fields.find((f) => f.systemPurpose === domain.FieldSystemPurpose.XeroItem);

        const itemFieldRequired = !!itemField && requiredFields.fieldIds.includes(itemField.id);
        const accountField = context.fields.find((f) => f.systemPurpose === domain.FieldSystemPurpose.XeroAccount);

        const accountFieldRequired =
            (!!accountField && requiredFields.fieldIds.includes(accountField.id)) || isApproverMode;
        const taxField = context.fields.find((f) => f.systemPurpose === domain.FieldSystemPurpose.XeroTax);
        const taxAmountField = context.fields.find(
            (f) => f.systemPurpose === domain.FieldSystemPurpose.XeroLineTaxAmount
        );

        const taxFieldRequired = !!taxField && requiredFields.fieldIds.includes(taxField.id);
        const taxAmountFieldRequired = !!taxAmountField && requiredFields.fieldIds.includes(taxAmountField?.id);

        return [
            ...(isApproverMode
                ? []
                : [
                      {
                          id: 'checkbox',
                          name: '',
                          fixedWidth: 40,
                          headerCell: (lineItems) => {
                              const checked = lineItems.every((li) => li.checked);
                              const triState = lineItems.some((li) => li.checked);

                              const handleClick = () => {
                                  const nextState = !checked;

                                  dispatch(changeXeroLineItemAllChecked(nextState));
                              };

                              return (
                                  <div onClick={handleClick}>
                                      <Checkbox
                                          size='size-18'
                                          triState={triState}
                                          checked={checked}
                                          disabled={ocrIsLoading}
                                      />
                                  </div>
                              );
                          },
                          cell: (li) => {
                              return (
                                  <div
                                      onClick={() => {
                                          dispatch(changeXeroLineItemChecked(li.id, !li.checked));
                                      }}
                                  >
                                      <Checkbox disabled={ocrIsLoading} checked={li.checked} size='size-18' />
                                  </div>
                              );
                          },
                      } as ColumnDefinition<ExpandedXeroLineItem>,
                  ]),
            {
                id: 'item',
                name: messages.colNameItem,
                flexGrow: 1,
                readonly: getItemReadonly,
                invalid: (li) => itemFieldRequired && showValidationErrors && !li.emptyLike && !li.item,
                cell: (li: ExpandedXeroLineItem, { invalid, readonly }) => {
                    const initialLineItem = getInitialLineItem(li.id);

                    return readonly ? (
                        <ReadOnlyCell text={li.item && li.item.text} />
                    ) : (
                        <XeroLineItemItemCell
                            qa={qa('cell-editor')}
                            className={bem('cell', 'dropdown')}
                            invalid={invalid}
                            value={li.item || null}
                            staticValues={isApproverMode && initialLineItem?.item ? [initialLineItem.item] : []}
                            onChange={(newItem: domain.XeroItem | null) =>
                                dispatch(changeXeroLineItemsItem([li.id], newItem))
                            }
                            placeholder={messages.cellPlaceholderItem}
                        />
                    );
                },
            },
            {
                id: 'description',
                name: messages.colNameDescription,
                flexGrow: 2,
                readonly: () => ocrIsLoading || descriptionFieldPermission.disabled,
                invalid: (li) => showValidationErrors && !li.emptyLike && !li.description,
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.description} multiline />
                    ) : (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <TextAreaEditor
                                    qa={qa('cell-editor')}
                                    ref={registerFocusable}
                                    invalid={invalid}
                                    value={li.description || ''}
                                    maxLength={xeroConstants.descriptionMaxLength}
                                    onChange={(newDescription) => {
                                        dispatch(
                                            changeXeroLineItemDescription(
                                                li.id,
                                                stringHelpers.escapeSpecificExpressions(
                                                    newDescription,
                                                    xeroConstants.XERO_NOT_ALLOWED_SYMBOLS
                                                )
                                            )
                                        );
                                    }}
                                    placeholder={messages.cellPlaceholderDescription}
                                    theme='transparent'
                                    changeOnBlur
                                />
                            )}
                        </FocusImplementer>
                    ),
            },
            {
                id: 'qty',
                name: messages.colNameQty,
                alignRight: true,
                flexGrow: 0.5,
                readonly: () => ocrIsLoading || qtyFieldPermission.disabled,
                invalid: (li) => showValidationErrors && !li.emptyLike && li.qty == null,
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.qty != null ? intl.formatNumber(li.qty, 'auto') : ''} />
                    ) : (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <NumberEditor
                                    qa={qa('cell-editor')}
                                    ref={registerFocusable}
                                    allowFloat
                                    precision={xeroConstants.PRECISION_BIG}
                                    max={xeroConstants.TOTAL_MAX_AMOUNT}
                                    min={xeroConstants.TOTAL_MIN_AMOUNT}
                                    invalid={invalid}
                                    value={li.qty != null ? li.qty : null}
                                    onChange={(newQty: number | null) => dispatch(changeXeroLineItemQty(li.id, newQty))}
                                    placeholder={messages.cellPlaceholderQty}
                                    theme='transparent'
                                    align='right'
                                    displayFormat='currency-auto'
                                    allowCalculation
                                />
                            )}
                        </FocusImplementer>
                    ),
            },
            {
                id: 'unitPrice',
                name: messages.colNameUnitPrice,
                alignRight: true,
                flexGrow: 0.7,
                readonly: () => ocrIsLoading || unitPriceFieldPermission.disabled,
                invalid: (li) => showValidationErrors && !li.emptyLike && li.unitPrice == null,
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.unitPrice != null ? intl.formatNumber(li.unitPrice, 'auto') : ''} />
                    ) : (
                        <PriceCheckIndicator
                            actualPrice={li.unitPrice}
                            expectedPrice={calculateXeroLineItemExpectedPrice(integrationCode, li.item)}
                            disabled={
                                integrationCode !== domain.IntegrationCode.XeroBill &&
                                integrationCode !== domain.IntegrationCode.XeroPo &&
                                integrationCode !== domain.IntegrationCode.XeroQuote &&
                                integrationCode !== domain.IntegrationCode.XeroInvoice
                            }
                            className={bem('cell', 'unit-price')}
                            integrationType={domain.IntegrationType.Xero}
                            integrationCode={integrationCode}
                        >
                            {({ warningLevel }) => (
                                <FocusImplementer>
                                    {({ registerFocusable }) => (
                                        <>
                                            <NumberEditor
                                                qa={qa('cell-editor')}
                                                className={bem('cell-unit-editor')}
                                                ref={registerFocusable}
                                                allowFloat
                                                precision={xeroConstants.PRECISION_BIG}
                                                invalid={invalid}
                                                value={li.unitPrice != null ? li.unitPrice : null}
                                                max={xeroConstants.MAX_AMOUNT}
                                                min={xeroConstants.MIN_AMOUNT}
                                                onChange={(newUnitPrice: number | null) =>
                                                    dispatch(changeXeroLineItemUnitPrice(li.id, newUnitPrice))
                                                }
                                                placeholder={messages.cellPlaceholderUnitPrice}
                                                theme='transparent'
                                                align='right'
                                                displayFormat='currency-auto'
                                                allowCalculation
                                            />

                                            <div className={bem('cell-unit-indicator', warningLevel)} />
                                        </>
                                    )}
                                </FocusImplementer>
                            )}
                        </PriceCheckIndicator>
                    ),
            },
            ...(integrationCode === domain.IntegrationCode.XeroPo
                ? [
                      {
                          id: 'disc',
                          name: messages.colNameDiscount,
                          alignRight: true,
                          flexGrow: 0.5,
                          readonly: () => discountFieldPermission.disabled || ocrIsLoading,
                          invalid: (li) =>
                              showValidationErrors &&
                              discountFieldPermission.required &&
                              !numberHelpers.isNumber(li.discount),
                          cell: (li, { invalid, readonly }) =>
                              readonly ? (
                                  <ReadOnlyCell text={li.discount != null ? intl.formatNumber(li.discount, 2) : ''} />
                              ) : (
                                  <FocusImplementer>
                                      {({ registerFocusable }) => (
                                          <NumberEditor
                                              qa={qa('cell-editor')}
                                              ref={registerFocusable}
                                              allowFloat
                                              precision={xeroConstants.PRECISION_SMALL}
                                              min={xeroConstants.MIN_DISCOUNT_VALUE}
                                              max={xeroConstants.MAX_DISCOUNT_VALUE}
                                              invalid={invalid}
                                              value={li.discount != null ? li.discount : null}
                                              onChange={(newDiscount: number | null) =>
                                                  dispatch(changeXeroLineItemDiscount(li.id, newDiscount))
                                              }
                                              placeholder={messages.cellPlaceholderDiscount}
                                              theme='transparent'
                                              align='right'
                                              displayFormat='currency-2'
                                              changeOnBlur={false}
                                          />
                                      )}
                                  </FocusImplementer>
                              ),
                      } as ColumnDefinition<ExpandedXeroLineItem>,
                  ]
                : []),

            ...(integrationCode === domain.IntegrationCode.XeroInvoice ||
            integrationCode === domain.IntegrationCode.XeroQuote
                ? [
                      {
                          id: 'discInvoice',
                          name: messages.colNameDiscountInvoice,
                          alignRight: true,
                          flexGrow: 0.5,
                          title: messages.colTitleDiscountInvoice,
                          readonly: () => discountFieldPermission.disabled,
                          invalid: (li) => {
                              let value: number | null;

                              switch (li.discountType) {
                                  case DiscountType.Amount:
                                  case DiscountType.PreAmount:
                                      value = li.discountAmount || null;
                                      break;

                                  case DiscountType.Rate:
                                  case DiscountType.PreRate:
                                      value = li.discount || null;
                                      break;

                                  default:
                                      value = null;
                              }

                              return showValidationErrors && discountFieldPermission.required && !value;
                          },
                          cell: (li, { invalid, readonly }) => {
                              let value: number | null;

                              switch (li.discountType) {
                                  case DiscountType.Amount:
                                  case DiscountType.PreAmount:
                                      value = li.discountAmount || null;
                                      break;

                                  case DiscountType.Rate:
                                  case DiscountType.PreRate:
                                      value = li.discount || null;
                                      break;

                                  default:
                                      value = null;
                              }

                              return readonly ? (
                                  <ReadOnlyCell text={value !== null ? intl.formatNumber(value, 2) : ''} />
                              ) : (
                                  <FocusImplementer>
                                      {({ registerFocusable }) => (
                                          <XeroDiscountCell
                                              ref={registerFocusable}
                                              li={li}
                                              invalid={invalid}
                                              value={value}
                                              discountType={li.discountType as DiscountType}
                                          />
                                      )}
                                  </FocusImplementer>
                              );
                          },
                      } as ColumnDefinition<ExpandedXeroLineItem>,
                  ]
                : []),
            {
                id: 'account',
                name: messages.colNameAccount,
                flexGrow: 1,
                readonly: getAccountReadonly,
                invalid: (li) =>
                    (isApproverMode || !!li.computedAmount) &&
                    accountFieldRequired &&
                    showValidationErrors &&
                    ((!li.emptyLike && !li.account) || li.invalidAccount),
                invalidText: (li) => li.invalidAccountText,
                cell: (li: ExpandedXeroLineItem, { invalid, readonly }) => {
                    const initialLineItem = getInitialLineItem(li.id);

                    let items: Reference[] | Identifiable[] =
                        isApproverMode &&
                        initialLineItem?.account &&
                        !xeroContext.accounts.some((account) => account.id === initialLineItem?.account?.id)
                            ? uniqBy([...xeroContext.accounts, initialLineItem.account], 'id')
                            : xeroContext.accounts;

                    let showMultipleAccountsWarning = false;

                    if (sortedOcrLineItems) {
                        items = prioritizeAndMapDropdownItems(items, sortedOcrLineItems?.[li.lineOrder]?.accountIds);
                        showMultipleAccountsWarning = (sortedOcrLineItems?.[li.lineOrder]?.accountIds || []).length > 1;
                    }

                    const warningLevel = showMultipleAccountsWarning ? WarningLevel.Yellow : WarningLevel.None;

                    return (
                        <WarningIndicator
                            show={showMultipleAccountsWarning}
                            content={messages.multipleAccountsWarning({
                                contactDisplayName: getContactDisplayNameByIntegrationCode(integrationCode),
                                requestName: stringHelpers.toLowerCaseWithPreservedAbbreviations(
                                    selectors.template.getTemplateDisplayNameByCode(integrationCode, true)
                                ),
                            })}
                        >
                            {readonly ? (
                                <>
                                    <ReadOnlyCell text={(!!li.computedAmount && li.account && li.account.text) || ''} />

                                    <div className={bem('cell-unit-indicator', warningLevel)} />
                                </>
                            ) : (
                                <XeroLineItemDropdownCell
                                    qa={qa('cell-editor')}
                                    className={bem('cell', 'dropdown')}
                                    items={items}
                                    value={li.account || null}
                                    invalid={invalid}
                                    onChange={(newAccount: domain.XeroAccount | null) =>
                                        dispatch(
                                            changeXeroLineItemsAccount({
                                                lineItemIds: [li.id],
                                                newAccount,
                                                recalculateTax: !isEditorMode && !isApproverMode,
                                            })
                                        )
                                    }
                                    defaultValueOnFocus={contact?.purchase?.account}
                                    placeholder={messages.cellPlaceholderAccount}
                                    afterContent={<div className={bem('cell-unit-indicator', warningLevel)} />}
                                />
                            )}
                        </WarningIndicator>
                    );
                },
            },
            {
                id: 'taxRate',
                name: messages.colNameTaxRate,
                flexGrow: 1,
                readonly: getTaxRateReadonly,
                invalid: (li) =>
                    (isApproverMode || !!li.computedAmount) &&
                    taxFieldRequired &&
                    showValidationErrors &&
                    !li.emptyLike &&
                    ((lineAmountType !== domain.LineAmountType.NoTax && !li.tax) ||
                        li.invalidTax ||
                        li.invalidAccountTaxMatch),
                invalidText: (li) => li.invalidTaxText || li.invalidAccountTaxMatchText,
                cell: (li: ExpandedXeroLineItem, { invalid, readonly }) => {
                    // Always show empty tax if amountType is noTax
                    const tax =
                        lineAmountType === domain.LineAmountType.NoTax
                            ? li.emptyLike
                                ? undefined
                                : xeroContext.emptyTaxCode
                            : li.tax;
                    const taxCodes = filterTaxCodesByAccountClass(xeroContext.taxCodes, li.account);
                    const initialLineItem = getInitialLineItem(li.id);

                    const initialTax =
                        lineAmountType === domain.LineAmountType.NoTax
                            ? initialLineItem?.emptyLike
                                ? undefined
                                : xeroContext.emptyTaxCode
                            : initialLineItem?.tax;

                    let items: Reference[] | Identifiable[] =
                        isApproverMode && initialTax && !taxCodes.some((taxCode) => taxCode.id === initialTax.id)
                            ? uniqBy([...taxCodes, initialTax], 'id')
                            : taxCodes;

                    let showMultipleTaxCodesWarning = false;

                    if (sortedOcrLineItems) {
                        items = prioritizeAndMapDropdownItems(items, sortedOcrLineItems?.[li.lineOrder]?.taxTypes);
                        showMultipleTaxCodesWarning = (sortedOcrLineItems?.[li.lineOrder]?.taxTypes || []).length > 1;
                    }

                    const warningLevel = showMultipleTaxCodesWarning ? WarningLevel.Yellow : WarningLevel.None;

                    return (
                        <WarningIndicator
                            show={showMultipleTaxCodesWarning}
                            content={messages.multipleTaxCodesWarning({
                                contactDisplayName: getContactDisplayNameByIntegrationCode(integrationCode),
                                requestName: stringHelpers.toLowerCaseWithPreservedAbbreviations(
                                    selectors.template.getTemplateDisplayNameByCode(integrationCode, true)
                                ),
                            })}
                        >
                            {readonly ? (
                                <>
                                    <ReadOnlyCell
                                        text={(!!li.computedAmount && tax && tax.text) || ''}
                                        invalid={invalid}
                                    />

                                    <div className={bem('cell-unit-indicator', warningLevel)} />
                                </>
                            ) : (
                                <XeroLineItemDropdownCell
                                    qa={qa('cell-editor')}
                                    className={bem('cell', 'dropdown')}
                                    items={items}
                                    value={tax || null}
                                    invalid={invalid}
                                    onChange={(newTax: domain.XeroTaxCode | null) =>
                                        dispatch(changeXeroLineItemsTax([li.id], newTax))
                                    }
                                    placeholder={messages.cellPlaceholderTaxRate}
                                    afterContent={<div className={bem('cell-unit-indicator', warningLevel)} />}
                                />
                            )}
                        </WarningIndicator>
                    );
                },
            },

            ...(xeroContext.requiresTaxAdjustments
                ? [
                      {
                          id: 'taxAmount',
                          name: messages.colNameTaxAmount,
                          alignRight: true,
                          flexGrow: 0.5,
                          readonly: (li) => !li.computedAmount || ocrIsLoading || taxAmountFieldPermission.disabled,
                          invalid: (li) =>
                              taxAmountFieldRequired && showValidationErrors && !(li.taxAmount || li.computedTaxAmount),
                          cell: (li, { invalid, readonly }) => {
                              const value = li.taxAmount != null ? li.taxAmount : li.computedTaxAmount;

                              let showDifferentTaxAmountWarning = false;

                              const recognizedTaxAmount = sortedOcrLineItems?.[li.lineOrder]?.taxAmount;

                              if (value && recognizedTaxAmount) {
                                  showDifferentTaxAmountWarning = value !== recognizedTaxAmount;
                              }

                              const warningLevel = showDifferentTaxAmountWarning
                                  ? WarningLevel.Yellow
                                  : WarningLevel.None;

                              return (
                                  <WarningIndicator
                                      show={showDifferentTaxAmountWarning}
                                      content={
                                          <TwoDifferentValuesWarning
                                              value1={value}
                                              value2={recognizedTaxAmount}
                                              label1={messages.differentTaxAmountWarningLabel1({ templateName })}
                                              label2={messages.differentTaxAmountWarningLabel2}
                                              description={messages.differentTaxAmountWarningDescription({
                                                  templateName,
                                              })}
                                          />
                                      }
                                  >
                                      {readonly ? (
                                          <>
                                              <ReadOnlyCell
                                                  text={
                                                      value != null && !!li.computedAmount
                                                          ? intl.formatNumber(value, 2)
                                                          : ''
                                                  }
                                              />

                                              <div className={bem('cell-unit-indicator', warningLevel)} />
                                          </>
                                      ) : (
                                          <FocusImplementer>
                                              {({ registerFocusable }) => (
                                                  <>
                                                      <NumberEditor
                                                          qa={qa('cell-editor')}
                                                          ref={registerFocusable}
                                                          allowFloat
                                                          precision={xeroConstants.PRECISION_SMALL}
                                                          invalid={invalid}
                                                          min={xeroConstants.MIN_AMOUNT}
                                                          max={xeroConstants.MAX_AMOUNT}
                                                          value={value || null}
                                                          onChange={(newTaxAmount: number | null) =>
                                                              dispatch(changeXeroLineItemTaxAmount(li.id, newTaxAmount))
                                                          }
                                                          placeholder={messages.cellPlaceholderTaxAmount}
                                                          theme='transparent'
                                                          align='right'
                                                          displayFormat='currency-2'
                                                          className={bem('cell-unit-editor')}
                                                      />

                                                      <div className={bem('cell-unit-indicator', warningLevel)} />
                                                  </>
                                              )}
                                          </FocusImplementer>
                                      )}
                                  </WarningIndicator>
                              );
                          },
                      } as ColumnDefinition<ExpandedXeroLineItem>,
                  ]
                : []),
            ...xeroContext.trackingCategories.map(
                (tc): ColumnDefinition<ExpandedXeroLineItem> => ({
                    id: tc.category.id,
                    name: tc.category.text,
                    flexGrow: 1,
                    readonly: () => getTrackingCategoryReadonly(tc.category.id),
                    invalid: (li) => {
                        const trackingItem = li.tracking.find((t) => t.category.id === tc.category.id);

                        return (
                            showValidationErrors &&
                            ((!li.emptyLike && requiredFields.fieldIds.includes(tc.category.id) && !trackingItem) ||
                                li.invalidTrackingCategories.some((itc) => itc.categoryId === tc.category.id))
                        );
                    },
                    invalidText: (li) => {
                        const error = li.invalidTrackingCategories.find((x) => x.categoryId === tc.category.id);

                        if (error) {
                            return error.errorText;
                        }

                        return undefined;
                    },
                    cell: (li, { readonly, invalid }) => {
                        const trackingItem = li.tracking.find((t) => t.category.id === tc.category.id);
                        const initialLineItem = getInitialLineItem(li.id);
                        const initialTrackingItem = initialLineItem?.tracking.find(
                            (t) => t.category.id === tc.category.id
                        );

                        let items: Reference[] | Identifiable[] =
                            isApproverMode &&
                            initialTrackingItem?.value &&
                            !tc.options.some((option) => option.id === initialTrackingItem?.value.id)
                                ? uniqBy([...tc.options, initialTrackingItem.value], 'id')
                                : tc.options;

                        let showMultipleTrackingCategoriesWarning = false;

                        if (sortedOcrLineItems) {
                            const category = sortedOcrLineItems?.[li.lineOrder]?.trackingCategoryOptions?.find(
                                (category) => category.categoryId === tc.category.id
                            );

                            if (category) {
                                items = prioritizeAndMapDropdownItems(items, category.optionIds);

                                showMultipleTrackingCategoriesWarning = category?.optionIds?.length > 1;
                            }
                        }

                        const warningLevel = showMultipleTrackingCategoriesWarning
                            ? WarningLevel.Yellow
                            : WarningLevel.None;

                        return (
                            <WarningIndicator
                                show={showMultipleTrackingCategoriesWarning}
                                content={messages.multipleTrackingCategoriesWarning({
                                    categoryName: tc.category.text,
                                    contactDisplayName: getContactDisplayNameByIntegrationCode(integrationCode),
                                    requestName: stringHelpers.toLowerCaseWithPreservedAbbreviations(
                                        selectors.template.getTemplateDisplayNameByCode(integrationCode, true)
                                    ),
                                })}
                            >
                                {readonly ? (
                                    <>
                                        <ReadOnlyCell
                                            text={trackingItem && trackingItem.value && trackingItem.value.text}
                                        />

                                        <div className={bem('cell-unit-indicator', warningLevel)} />
                                    </>
                                ) : (
                                    <XeroLineItemDropdownCell
                                        qa={qa('cell-editor')}
                                        className={bem('cell', 'dropdown')}
                                        invalid={invalid}
                                        items={items}
                                        value={trackingItem ? trackingItem.value : null}
                                        onChange={(newValue: Reference | null) =>
                                            dispatch(
                                                changeXeroLineItemsTrackingCategoryValue(
                                                    [li.id],
                                                    tc.category.id,
                                                    newValue
                                                )
                                            )
                                        }
                                        defaultValueOnFocus={
                                            contact?.purchase?.trackingCategories.find(
                                                (x) => x.category.id === tc.category.id
                                            )?.value
                                        }
                                        placeholder={messages.cellPlaceholderTrackingCategory({
                                            categoryName: tc.category.text.toLowerCase(),
                                        })}
                                        afterContent={<div className={bem('cell-unit-indicator', warningLevel)} />}
                                    />
                                )}
                            </WarningIndicator>
                        );
                    },
                })
            ),
            {
                id: 'amount',
                name: messages.colNameAmount,
                flexGrow: 0.5,
                alignRight: true,
                readonly: () => true,
                invalid: (li) => li.invalidAmount,
                cell: (li: ExpandedXeroLineItem) => {
                    const amountText = li.computedAmount != null ? intl.formatNumber(li.computedAmount, 2) : undefined;
                    const amountTitle = li.invalidAmount ? li.invalidAmountText : amountText;

                    let showDifferentAmountWarning = false;

                    const amount = sortedOcrLineItems?.[li.lineOrder]?.amount;

                    const recognizedAmount = typeof amount !== 'number' ? amount?.sourceValue : undefined;

                    if (recognizedAmount && li.computedTotalAmount) {
                        showDifferentAmountWarning = li.computedTotalAmount !== recognizedAmount;
                    }

                    const warningLevel = showDifferentAmountWarning ? WarningLevel.Yellow : WarningLevel.None;

                    return (
                        <WarningIndicator
                            show={showDifferentAmountWarning}
                            content={
                                <TwoDifferentValuesWarning
                                    value1={li.computedTotalAmount}
                                    value2={recognizedAmount}
                                    label1={messages.differentAmountWarningLabel1({ templateName })}
                                    label2={messages.differentAmountWarningLabel2}
                                    description={messages.differentAmountWarningDescription({ templateName })}
                                />
                            }
                        >
                            <div data-qa={qa('cell-value')} className={bem('cell', 'amount')} title={amountTitle}>
                                {amountText}
                            </div>

                            <div className={bem('cell-unit-indicator', warningLevel)} />
                        </WarningIndicator>
                    );
                },
            },
        ];
    }, [
        context.fields,
        requiredFields.fieldIds,
        isApproverMode,
        getItemReadonly,
        integrationCode,
        getAccountReadonly,
        getTaxRateReadonly,
        xeroContext.requiresTaxAdjustments,
        xeroContext.trackingCategories,
        xeroContext.accounts,
        xeroContext.emptyTaxCode,
        xeroContext.taxCodes,
        ocrIsLoading,
        dispatch,
        showValidationErrors,
        getInitialLineItem,
        descriptionFieldPermission.disabled,
        qtyFieldPermission.disabled,
        unitPriceFieldPermission.disabled,
        discountFieldPermission.disabled,
        discountFieldPermission.required,
        sortedOcrLineItems,
        contact?.purchase?.account,
        contact?.purchase?.trackingCategories,
        isEditorMode,
        lineAmountType,
        taxAmountFieldPermission.disabled,
        templateName,
        getTrackingCategoryReadonly,
    ]);

    const handleManualChangeExchangeRate = (value?: number) => {
        if (value) {
            dispatch(
                changeExchangeRateParams({
                    exchangeRate: value,
                    exchangeRateSource: domain.ExchangeRateSource.Manual,
                    exchangeRateDate: null,
                })
            );
        } else {
            dispatch(
                changeExchangeRateParams({
                    exchangeRate: null,
                    exchangeRateSource: domain.ExchangeRateSource.None,
                    exchangeRateDate: null,
                })
            );
        }
    };

    const currencyOptions = useMemo(() => {
        return isApproverMode &&
            initialCurrencyValue.current &&
            !xeroContext.currencies.some((currency) => currency.id === initialCurrencyValue.current?.id)
            ? uniqBy([...xeroContext.currencies, initialCurrencyValue.current], 'id')
            : xeroContext.currencies;
    }, [isApproverMode, xeroContext.currencies]);

    const lineAmountTypeOptions = useMemo(
        () =>
            lineAmountTypeReferences.filter(({ id, backendId }) => {
                if (!isApproverMode) return true;

                const isValid = checkFieldValueWithRules({
                    rules: participantWithEditPermission?.editPermissions || [],
                    fieldSystemPurposeOrId: domain.FieldSystemPurpose.XeroLineAmountType,
                    fieldValueId: backendId,
                });

                return isValid || id === initialLineAmountType.current;
            }),
        [isApproverMode, participantWithEditPermission?.editPermissions]
    );

    const differentCurrency = ocrData?.document?.currencyCode !== currency;
    const nonExistCurrency = currencyOptions.every((currence) => currence.id !== ocrData?.document?.currencyCode);

    const showCurrencyWarning = Boolean(ocrData?.document?.currencyCode && (differentCurrency || nonExistCurrency));

    const onRemoveLine = useCallback((lineItemId: string) => dispatch(removeXeroLineItems([lineItemId])), [dispatch]);
    const onCloneLine = useCallback((lineItemId: string) => dispatch(cloneXeroLineItems([lineItemId])), [dispatch]);
    const onAddLine = useCallback(() => dispatch(addXeroLineItem()), [dispatch]);
    const onReorder = useCallback(
        (oldIndex: number, newIndex: number) => dispatch(reorderXeroLineItems(oldIndex, newIndex)),
        [dispatch]
    );

    const onChangeExchangeRate = useCallback(
        (params: ExchangeRateParams) => {
            dispatch(changeExchangeRateParams(params));
        },
        [dispatch]
    );

    return (
        <SectionContainer
            className={bem.add(className)()}
            qa={qa()}
            noPadding
            title={
                <div className={bem('header')}>
                    <div className={bem('header-text')}>{messages.headerText}</div>

                    <div className={bem('header-fields')}>
                        {request.exchangeRateSource === domain.ExchangeRateSource.Manual ? (
                            <StyledCurrencyDisclaimer />
                        ) : (
                            <StyledExchangeRateWithUpdate
                                onChange={onChangeExchangeRate}
                                requestId={request.id}
                                companyId={request.companyId}
                                date={request.details.date}
                                currency={request.currency}
                                companyCurrency={request.companyCurrency}
                                isXero
                            />
                        )}

                        {isExchangeRateInputAvailable && (
                            <CurrencyExchangeRateInput
                                selectedCurrency={statics.currency.findCurrencyByCode(currency) || null}
                                defaultCurrency={statics.currency.findCurrencyByCode(companyCurrency) || null}
                                exchangeRateValue={
                                    request.exchangeRateSource === domain.ExchangeRateSource.Manual
                                        ? exchangeRate
                                        : null
                                }
                                onExchangeRateChange={handleManualChangeExchangeRate}
                                disabled={isEditorMode || ocrIsLoading || exchangeRateFieldPermission.disabled}
                                showValidationErrors={showValidationErrors}
                                required={exchangeRateFieldPermission.required}
                            />
                        )}

                        <Tooltip
                            disabled={!showCurrencyWarning}
                            width='max-content'
                            placement='top'
                            activator={
                                <PlainDataProvider items={currencyOptions}>
                                    <DropdownEditor
                                        qa={qa('currency-editor')}
                                        className={bem('header-fields-currency')}
                                        warning={showCurrencyWarning}
                                        disabled={isEditorMode || ocrIsLoading || currencyFieldPermission.disabled}
                                        value={statics.currency.findCurrencyByCode(currency) || null}
                                        onChange={({ id }: XeroCurrency) => {
                                            dispatch(changeCurrency(id));

                                            if (id === companyCurrency) {
                                                dispatch(
                                                    changeExchangeRateParams({
                                                        exchangeRate: null,
                                                        exchangeRateSource: domain.ExchangeRateSource.None,
                                                        exchangeRateDate: null,
                                                    })
                                                );
                                            }
                                        }}
                                        buttonComponent={DropdownEditor.NoFilterButton}
                                    />
                                </PlainDataProvider>
                            }
                        >
                            {differentCurrency ? (
                                <TwoDifferentValuesWarning
                                    value1={currency}
                                    value2={ocrData?.document?.currencyCode}
                                    label1={messages.differentCurrencyWarningLabel1({ templateName })}
                                    label2={messages.differentCurrencyWarningLabel2}
                                    description={messages.differentCurrencyWarningDescription({ templateName })}
                                />
                            ) : (
                                messages.nonExistCurrencyWarning({ templateName })
                            )}
                        </Tooltip>

                        <div className={bem('header-fields-label')}>{messages.lineAmountTypeLabel}</div>

                        <PlainDataProvider items={lineAmountTypeOptions}>
                            <DropdownEditor
                                qa={qa('line-amount-type-editor')}
                                className={bem('header-fields-line-amount-type')}
                                disabled={isEditorMode || ocrIsLoading || lineAmountTypeFieldPermission.disabled}
                                value={findLineAmountType(lineAmountType)}
                                onChange={(v: { id: domain.LineAmountType }) =>
                                    dispatch(changeXeroLineAmountType(v.id))
                                }
                                buttonComponent={DropdownEditor.NoFilterButton}
                            />
                        </PlainDataProvider>
                    </div>

                    {isShowGroupButtons && (
                        <div className={bem('group-actions')}>
                            {isGroupEditVisible && (
                                <Button preset='small' disabled={ocrIsLoading} execute={openGroupEdit}>
                                    {messages.groupEdit}
                                </Button>
                            )}

                            {isGroupCloneVisible && (
                                <Button preset='small' disabled={ocrIsLoading} execute={handleGroupClone}>
                                    {messages.groupClone}
                                </Button>
                            )}

                            {isGroupRemoveVisible && (
                                <Button preset='small' disabled={ocrIsLoading} execute={handleGroupRemove}>
                                    {messages.groupRemove}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            }
        >
            <div className={bem('content')}>
                <LineItemsTable
                    columnDefinitions={tableDef}
                    lineItems={lineItems}
                    invalid={showValidationErrors && lineItems.every((li) => li.empty)}
                    onReorder={onReorder}
                    onRemoveLine={onRemoveLine}
                    onCloneLine={onCloneLine}
                    onAddLine={onAddLine}
                    canAddLine={!isEditorMode && !isApproverMode && !ocrIsLoading}
                />

                {isBelgradeUILineItemsTotalSection ? (
                    <XeroRequestItemsSummary bem={bem} qa={qa} request={request} ocrData={ocrData} />
                ) : (
                    <XeroLineItemsSectionFooterLegacy bem={bem} qa={qa} request={request} ocrData={ocrData} />
                )}
            </div>

            {isGroupEditPopupVisible && <LineItemsGroupEdit onClose={closeGroupEdit} items={groupItems} />}
        </SectionContainer>
    );
};

export default XeroLineItemsSection;
